import { useEffect, useState } from "react";
import { type useTranslate } from "@refinedev/core";

import { type AchtergrondStore, useAchtergrondDialoogOpen, useAchtergrondTaak } from "./achtergrondAi";
import { verstreken } from "../communicatie/editor/importTaak";
// Dezelfde melding als bij het nabouwen van een document, dus dezelfde opmaak.
import "../communicatie/editor/importTaak.css";

type Translate = ReturnType<typeof useTranslate>;

/**
 * De melding rechtsonder terwijl de AI een voorstel maakt, en als het klaar is. Hangt in de app, buiten
 * elk scherm, zodat hij meereist als je ondertussen naar een dier of een factuur gaat. {@code prefix} kiest
 * de teksten ("takenAi" of "wizardAi").
 */
export function AiVoorstelMelding<R>({
  store,
  prefix,
  t,
  onBekijken,
}: {
  store: AchtergrondStore<R>;
  prefix: string;
  t: Translate;
  onBekijken: () => void;
}) {
  const taak = useAchtergrondTaak(store);
  const dialoogOpen = useAchtergrondDialoogOpen(store);
  const [nu, setNu] = useState(() => Date.now());

  useEffect(() => {
    if (taak.status !== "bezig") return;
    const id = setInterval(() => setNu(Date.now()), 1000);
    return () => clearInterval(id);
  }, [taak.status]);

  if (taak.status === "leeg" || dialoogOpen) return null;

  const kort = taak.beschrijving.length > 60 ? `${taak.beschrijving.slice(0, 60).trimEnd()}…` : taak.beschrijving;
  const foutTekst =
    taak.status !== "fout"
      ? ""
      : taak.code === 401
        ? t("aiVoorstel.melding.sessie")
        : (taak.melding ?? t(`${prefix}.melding.fout`));

  return (
    <div className={`comm-importmelding comm-importmelding--${taak.status}`} role="status" aria-live="polite">
      {taak.status === "bezig" ? (
        <span className="comm-import__spinner" aria-hidden="true" />
      ) : (
        <span className="comm-importmelding__icoon" aria-hidden="true">
          {taak.status === "klaar" ? "✓" : "!"}
        </span>
      )}
      <div className="comm-importmelding__tekst">
        <strong>
          {taak.status === "bezig" && t(`${prefix}.melding.bezig`)}
          {taak.status === "klaar" && t(`${prefix}.melding.klaar`)}
          {taak.status === "fout" && foutTekst}
          {taak.status === "bezig" && <span className="comm-import__teller">{verstreken(taak.gestart, nu)}</span>}
        </strong>
        <span>“{kort}”</span>
      </div>
      {taak.status === "klaar" && (
        <button type="button" className="comm-importmelding__knop" onClick={onBekijken}>
          {t("aiVoorstel.melding.bekijken")}
        </button>
      )}
      <button
        type="button"
        className="comm-importmelding__sluit"
        aria-label={t(taak.status === "bezig" ? "aiVoorstel.melding.stoppen" : "common.close")}
        title={t(taak.status === "bezig" ? "aiVoorstel.melding.stoppen" : "common.close")}
        onClick={() => store.stop()}
      >
        ×
      </button>
    </div>
  );
}
